import {Injectable} from '@angular/core';
import {firstValueFrom, map} from 'rxjs';
import {HttpParams} from '@angular/common/http';
import {GetService} from '@shared/api/services/get.service';
import {Pagination, PaginationResponse} from '@models/pagination.model';
import {
    Contract,
    ContractAdminListItem,
    AdminUserLookupItem,
    AdminUserLookupRole,
    ContractChange,
    ContractListFilters
} from '@shared/api/models/query/contract.model';
import {
    ContractLinkedOrganisation,
    ContractOrganisationsQuery
} from '@shared/api/models/query/organisation.model';
import {ContractOrganisationsAdd} from '@shared/api/models/command/organisation.model';
import {
    ContractAdminsAdd,
    ContractCreate,
    ContractDeactivate,
    ContractUpdate
} from '@shared/api/models/command/contract.model';
import {
    ContractRequestError,
    isAlreadyNotified,
    listAdminUsersByRole,
    paginationParams,
    responseBody
} from '@shared/api/services/v2/api-request';

export {ContractRequestError, isAlreadyNotified};

/**
 * UNIVAPLAT-3514: integration layer for the admin contract endpoints
 * (`/api/v2/contracts`, backend contract UNIVAPLAT-3498).
 *
 * Promise-based per codebase convention; components never call HttpClient. Errors
 * (403, 404, 409, 422) propagate to the caller untouched: the backend owns the
 * user-facing text. The service holds no contract admin or log data between calls
 * and never logs any of it.
 */
@Injectable({
    providedIn: 'root'
})
export class ContractsService extends GetService<Contract, Contract> {

    protected override get domainEntity() {
        return 'contracts'
    };

    /** `GET /api/v2/contracts`. `show*` flags are sent only when true. */
    async list(pagination: Pagination = new Pagination(), filters: ContractListFilters = {}): Promise<PaginationResponse<Contract>> {
        let params = new HttpParams()
            .set('size', pagination.size)
            .set('start', pagination.start * pagination.size);
        if (pagination.sort) {
            params = params.set('sort', pagination.sort.prop).set('order', pagination.sort.order);
        }
        if (filters.title) {
            params = params.set('title', filters.title);
        }
        if (filters.code) {
            params = params.set('code', filters.code);
        }
        if (filters.serviceProvider) {
            params = params.set('serviceProvider', filters.serviceProvider);
        }
        if (filters.showDeactivated) {
            params = params.set('showDeactivated', 'true');
        }
        if (filters.showDeleted) {
            params = params.set('showDeleted', 'true');
        }
        const result = this.http.get<PaginationResponse<Contract>>(this.urlToEntity, {params, observe: 'events'})
            .pipe(responseBody(), map((response) => ({
                data: response?.data ?? [],
                pagination: response?.pagination
            })));
        return firstValueFrom(result);
    }

    /** `GET /api/v2/contracts/{id}` (inherited `getById` is the same call). */
    async get(id: number): Promise<Contract> {
        return firstValueFrom(this.http.get<{ data: Contract }>(`${this.urlToEntity}/${id}`, {observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    /** `POST /api/v2/contracts`. Errors: 403, 409 (code already exists), 422. */
    async create(body: ContractCreate): Promise<{ id: number }> {
        return firstValueFrom(this.http.post<{ data: { id: number } }>(this.urlToEntity, body, {observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    /** `PUT /api/v2/contracts/{id}`. Errors: 403, 404, 409, 422. */
    async update(id: number, body: ContractUpdate): Promise<{ id: number }> {
        return firstValueFrom(this.http.put<{ data: { id: number } }>(`${this.urlToEntity}/${id}`, body, {observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    /** `DELETE /api/v2/contracts/{id}`; answers 204. Errors: 403, 404, 409 (linked to organisations). */
    async delete(id: number): Promise<void> {
        await firstValueFrom(this.http.delete<void>(`${this.urlToEntity}/${id}`, {observe: 'events'})
            .pipe(responseBody()));
    }

    /** `PATCH /api/v2/contracts/{id}/deactivate`, optional reason. Errors: 403, 404, 409, 422. */
    async deactivate(id: number, reason?: string): Promise<{ id: number }> {
        const body: ContractDeactivate = reason !== undefined ? {reason} : {};
        return firstValueFrom(this.http.patch<{ data: { id: number } }>(`${this.urlToEntity}/${id}/deactivate`, body, {observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    /** `PATCH /api/v2/contracts/{id}/reactivate`, no body. Errors: 403, 404, 409. */
    async reactivate(id: number): Promise<{ id: number }> {
        return firstValueFrom(this.http.patch<{ data: { id: number } }>(`${this.urlToEntity}/${id}/reactivate`, null, {observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    /** `GET /api/v2/contracts/{id}/logs`, newest first unless a sort is given. */
    async listLogs(id: number, pagination: Pagination = new Pagination()): Promise<PaginationResponse<ContractChange>> {
        let params = new HttpParams()
            .set('size', pagination.size)
            .set('start', pagination.start * pagination.size);
        if (pagination.sort) {
            params = params.set('sort', pagination.sort.prop).set('order', pagination.sort.order);
        }
        const result = this.http.get<PaginationResponse<ContractChange>>(`${this.urlToEntity}/${id}/logs`, {params, observe: 'events'})
            .pipe(responseBody(), map((response) => ({
                data: response?.data ?? [],
                pagination: response?.pagination
            })));
        return firstValueFrom(result);
    }

    /** `GET /api/v2/contracts/{id}/admins`, flat `{data}`. */
    async listAdmins(id: number): Promise<ContractAdminListItem[]> {
        return firstValueFrom(this.http.get<{ data: ContractAdminListItem[] }>(`${this.urlToEntity}/${id}/admins`, {observe: 'events'})
            .pipe(responseBody(), map((response) => response?.data ?? [])));
    }

    /** `POST /api/v2/contracts/{id}/admins`. Errors: 403, 404, 409, 422. */
    async addAdmins(id: number, userIds: number[]): Promise<{ id: number }> {
        const body: ContractAdminsAdd = {contractAdmins: userIds};
        return firstValueFrom(this.http.post<{ data: { id: number } }>(`${this.urlToEntity}/${id}/admins`, body, {observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    /** `DELETE /api/v2/contracts/{id}/admins/{adminId}`; `adminId` is `ContractAdminListItem.id`. */
    async removeAdmin(id: number, adminId: number): Promise<void> {
        await firstValueFrom(this.http.delete<void>(`${this.urlToEntity}/${id}/admins/${adminId}`, {observe: 'events'})
            .pipe(responseBody()));
    }

    /**
     * `GET /api/v2/contracts/{id}/organisations`. The linked list (default) is flat and has no
     * `pagination`; `linked: false` lists the organisations available to link, paginated, and
     * filterable by `name` and `code`. Errors: 403, 404, 422 (contract not active, unlinked list).
     */
    async listOrganisations(
        id: number,
        query: ContractOrganisationsQuery = {},
        pagination?: Pagination
    ): Promise<PaginationResponse<ContractLinkedOrganisation>> {
        let params = pagination ? paginationParams(pagination) : new HttpParams();
        if (query.linked !== undefined) {
            params = params.set('linked', query.linked ? 'true' : 'false');
        }
        if (query.name) {
            params = params.set('name', query.name);
        }
        if (query.code) {
            params = params.set('code', query.code);
        }
        return firstValueFrom(this.http.get<PaginationResponse<ContractLinkedOrganisation>>(`${this.urlToEntity}/${id}/organisations`, {params, observe: 'events'})
            .pipe(responseBody(), map((response) => ({
                data: response?.data ?? [],
                pagination: response?.pagination
            }))));
    }

    /** `POST /api/v2/contracts/{id}/organisations` once with every id. Errors: 400, 403, 404, 422. */
    async addOrganisations(id: number, organisationIds: number[]): Promise<{ id: number }> {
        const body: ContractOrganisationsAdd = {organisationIds};
        return firstValueFrom(this.http.post<{ data: { id: number } }>(`${this.urlToEntity}/${id}/organisations`, body, {observe: 'events'})
            .pipe(responseBody(), map((response) => response.data)));
    }

    /** `DELETE /api/v2/contracts/{id}/organisations/{organisationId}`; answers 204. */
    async removeOrganisation(id: number, organisationId: number): Promise<void> {
        await firstValueFrom(this.http.delete<void>(`${this.urlToEntity}/${id}/organisations/${organisationId}`, {observe: 'events'})
            .pipe(responseBody()));
    }

    /**
     * `GET /api/v2/users/admins?role=<role>` (general admin only on the backend): every enabled
     * user of that role. Defaults to the contract admin role. Different entity from `contracts`,
     * so the shared helper builds its own URL.
     */
    async listCandidateAdmins(role: AdminUserLookupRole = 'admin-contract'): Promise<AdminUserLookupItem[]> {
        return listAdminUsersByRole(this.http, role);
    }
}
